import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import {
  Building2, User, Mail, Phone, Globe, Users, Sparkles, ArrowRight, ShieldCheck,
  FolderKanban, MessageSquareWarning, PhoneCall, Ticket, BarChart3, CheckCircle2,
} from "lucide-react";
import DKFooter from "@/components/DKFooter";
import LanguageThemeSelector from "@/components/LanguageThemeSelector";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SECTEURS = [
  "Assurance", "Mutuelle", "Banque", "Administration publique", "Santé",
  "Télécom", "Éducation", "Retail / Distribution", "Industrie", "Services", "Autre",
];
const TAILLES = ["1-10", "11-50", "51-200", "201-500", "500+"];
const SOURCES = ["Google", "LinkedIn", "Recommandation", "Salon / Événement", "Publicité", "Autre"];

const FEATURES = [
  { key: "dossiers", label: "Gestion des dossiers administratifs", icon: FolderKanban },
  { key: "reclamations", label: "Réclamations & SLA", icon: MessageSquareWarning },
  { key: "appels", label: "Centre d'appels", icon: PhoneCall },
  { key: "tickets", label: "File d'attente / borne", icon: Ticket },
  { key: "analytics", label: "Analytics & KPI", icon: BarChart3 },
  { key: "ged", label: "GED & OCR de documents", icon: ShieldCheck },
];

export default function Inscription() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null); // {entreprise} on success
  const [form, setForm] = useState({
    entreprise: "",
    contact_nom: "",
    email: "",
    telephone: "",
    secteur: "",
    taille: "",
    pays: "France",
    fonctionnalites: [],
    source: "",
    message: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleFeat = (key) =>
    setForm((f) => ({
      ...f,
      fonctionnalites: f.fonctionnalites.includes(key)
        ? f.fonctionnalites.filter((x) => x !== key)
        : [...f.fonctionnalites, key],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        entreprise: form.entreprise.trim(),
        contact_nom: form.contact_nom.trim(),
        email: form.email.trim(),
        telephone: form.telephone.trim(),
        secteur: form.secteur || null,
        taille: form.taille || null,
        pays: form.pays || null,
        fonctionnalites: form.fonctionnalites,
        source: form.source || null,
        message: form.message.trim() || null,
      };
      const res = await axios.post(`${BACKEND_URL}/api/public/prospects`, payload);
      setSubmitted({ entreprise: res.data.entreprise });
      toast.success("Demande envoyée avec succès !");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Impossible d'envoyer la demande. Réessayez.";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-6">
        <LanguageThemeSelector variant="floating" className="!top-3 !right-3" />
        <Card className="max-w-lg w-full border-emerald-200 dark:border-emerald-900 bg-white dark:bg-slate-900" data-testid="inscription-success-card">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-950/50 grid place-items-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-600 dark:text-emerald-400" strokeWidth={1.75} />
            </div>
            <h1 className="font-display text-3xl font-bold text-slate-900 dark:text-slate-100 tracking-tight">
              Merci !
            </h1>
            <p className="text-slate-600 dark:text-slate-300 mt-3">
              Votre demande pour <b>{submitted.entreprise}</b> a bien été enregistrée. Notre équipe vous contactera sous <b>48 h ouvrées</b> pour organiser une démo et vous transmettre vos identifiants d&apos;accès.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                variant="outline"
                onClick={() => navigate("/login")}
                className="rounded-full"
                data-testid="inscription-goto-login"
              >
                J&apos;ai déjà un code d&apos;accès
              </Button>
              <Button
                onClick={() => setSubmitted(null)}
                className="rounded-full bg-blue-900 hover:bg-blue-800 text-white"
                data-testid="inscription-new-request"
              >
                Nouvelle demande
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <LanguageThemeSelector variant="floating" className="!top-3 !right-3" />

      {/* Hero */}
      <div className="relative overflow-hidden login-hero text-white">
        <div className="grain absolute inset-0 opacity-40" />
        <div className="relative z-10 max-w-6xl mx-auto px-6 py-16 md:py-24">
          <div className="flex items-center gap-3 mb-8">
            <Link to="/login" className="w-14 h-14 rounded-xl bg-white/95 backdrop-blur-md grid place-items-center border border-white/20 shadow-lg overflow-hidden p-1.5" data-testid="inscription-brand-logo">
              <img src="/dkconnect-logo.png" alt="DKCONNECT" className="w-full h-full object-contain" />
            </Link>
            <div>
              <div className="font-display font-bold text-2xl tracking-tight">DK CONNECT</div>
              <div className="text-[11px] uppercase tracking-[0.28em] text-blue-200">Gestion intégrée entreprise</div>
            </div>
          </div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-blue-200 mb-3">Demandez votre accès</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight max-w-3xl">
            Modernisez votre gestion administrative <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-violet-200">avec DKConnect</span>.
          </h1>
          <p className="mt-5 text-blue-100/85 text-[15px] max-w-2xl">
            Dossiers, réclamations, appels, file d&apos;attente et analytics dans une seule plateforme — pensée pour les administrations, mutuelles et services clients.
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-6 -mt-12 pb-16 relative z-20">
        <Card className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xl">
          <CardContent className="p-6 md:p-10">
            <div className="mb-6">
              <div className="text-[11px] uppercase tracking-[0.28em] text-blue-800 dark:text-blue-300 mb-2">Formulaire</div>
              <h2 className="font-display text-3xl font-bold text-slate-900 dark:text-slate-100 tracking-tight">
                Parlez-nous de votre entreprise
              </h2>
              <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">
                Notre équipe étudie votre demande sous 48 h ouvrées et vous contacte pour une démo personnalisée.
              </p>
            </div>

            <form onSubmit={submit} className="space-y-6" data-testid="inscription-form">
              {/* Entreprise / contact */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FieldWrap label="Nom de l'entreprise *" icon={Building2}>
                  <Input
                    required
                    minLength={2}
                    value={form.entreprise}
                    onChange={(e) => set("entreprise", e.target.value)}
                    placeholder="Ex : Mutuelle Générale"
                    data-testid="inscription-entreprise"
                  />
                </FieldWrap>
                <FieldWrap label="Nom du contact *" icon={User}>
                  <Input
                    required
                    minLength={2}
                    value={form.contact_nom}
                    onChange={(e) => set("contact_nom", e.target.value)}
                    placeholder="Ex : Marie Dupont"
                    data-testid="inscription-contact"
                  />
                </FieldWrap>
                <FieldWrap label="Email professionnel *" icon={Mail}>
                  <Input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    placeholder="contact@entreprise.fr"
                    data-testid="inscription-email"
                  />
                </FieldWrap>
                <FieldWrap label="Téléphone *" icon={Phone}>
                  <Input
                    required
                    minLength={6}
                    value={form.telephone}
                    onChange={(e) => set("telephone", e.target.value)}
                    placeholder="+33 1 23 45 67 89"
                    data-testid="inscription-telephone"
                  />
                </FieldWrap>
              </div>

              {/* Secteur / taille / pays */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-700 dark:text-slate-200">Secteur d&apos;activité</Label>
                  <Select value={form.secteur} onValueChange={(v) => set("secteur", v)}>
                    <SelectTrigger className="mt-1.5 h-11" data-testid="inscription-secteur">
                      <SelectValue placeholder="Sélectionner…" />
                    </SelectTrigger>
                    <SelectContent>
                      {SECTEURS.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-700 dark:text-slate-200 inline-flex items-center gap-2">
                    <Users className="w-4 h-4" /> Taille
                  </Label>
                  <Select value={form.taille} onValueChange={(v) => set("taille", v)}>
                    <SelectTrigger className="mt-1.5 h-11" data-testid="inscription-taille">
                      <SelectValue placeholder="Nombre de salariés" />
                    </SelectTrigger>
                    <SelectContent>
                      {TAILLES.map((s) => (
                        <SelectItem key={s} value={s}>{s} salariés</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <FieldWrap label="Pays" icon={Globe}>
                  <Input
                    value={form.pays}
                    onChange={(e) => set("pays", e.target.value)}
                    placeholder="Ex : France"
                    data-testid="inscription-pays"
                  />
                </FieldWrap>
              </div>

              {/* Fonctionnalités */}
              <div>
                <Label className="text-slate-700 dark:text-slate-200 inline-flex items-center gap-2">
                  <Sparkles className="w-4 h-4" /> Fonctionnalités qui vous intéressent
                </Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">
                  {FEATURES.map((f) => {
                    const checked = form.fonctionnalites.includes(f.key);
                    return (
                      <label
                        key={f.key}
                        className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition ${
                          checked
                            ? "border-blue-800 bg-blue-50 dark:bg-blue-950/30"
                            : "border-slate-200 dark:border-slate-700 hover:border-slate-300"
                        }`}
                        data-testid={`inscription-feat-${f.key}`}
                      >
                        <Checkbox checked={checked} onCheckedChange={() => toggleFeat(f.key)} />
                        <f.icon className="w-4 h-4 text-blue-800 dark:text-blue-300 shrink-0" strokeWidth={1.75} />
                        <span className="text-sm text-slate-700 dark:text-slate-200">{f.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Source + message */}
              <div>
                <Label className="text-slate-700 dark:text-slate-200">Comment nous avez-vous connus ?</Label>
                <Select value={form.source} onValueChange={(v) => set("source", v)}>
                  <SelectTrigger className="mt-1.5 h-11 md:max-w-xs" data-testid="inscription-source">
                    <SelectValue placeholder="Sélectionner…" />
                  </SelectTrigger>
                  <SelectContent>
                    {SOURCES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="msg" className="text-slate-700 dark:text-slate-200">Votre besoin en quelques mots</Label>
                <Textarea
                  id="msg"
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  placeholder="Décrivez-nous rapidement votre contexte, vos priorités, vos volumes…"
                  rows={4}
                  className="mt-1.5"
                  data-testid="inscription-message"
                />
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2 border-t border-slate-100 dark:border-slate-800">
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  En envoyant ce formulaire, vous acceptez d&apos;être contacté par DKConnect. Vos données sont traitées de manière confidentielle.
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto rounded-full bg-blue-900 hover:bg-blue-800 text-white h-11 px-6"
                  data-testid="inscription-submit"
                >
                  {loading ? "Envoi…" : (
                    <span className="inline-flex items-center gap-2">
                      Envoyer ma demande <ArrowRight className="w-4 h-4" />
                    </span>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <div className="mt-6 text-center text-sm text-slate-500 dark:text-slate-400">
          Déjà client ?{" "}
          <Link to="/login" className="text-blue-800 dark:text-blue-300 hover:underline font-medium" data-testid="inscription-login-link">
            Connectez-vous avec votre code d&apos;accès
          </Link>
        </div>
      </div>

      <DKFooter variant="compact" />
    </div>
  );
}

function FieldWrap({ label, icon: Icon, children }) {
  return (
    <div>
      <Label className="text-slate-700 dark:text-slate-200 inline-flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4" />} {label}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
